'use client';
import React from 'react';
import { Stack, Select, Text, Group, Button, Collapse, Table, Divider } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { DateTimePicker } from '@mantine/dates';
import { getRate } from '../../types/RATES';
import { Leg } from '../../types/trip';
import { calculatePactoLegsPayment } from '../../utils/payment';
import LegsTable from './LegsTable';
import { getSpainRegionOptions } from '../../utils/holidays';
import { useTranslation } from 'react-i18next';
import { isoToDateInAppTz } from '@/lib/dayjs';

type TripEditFormModalProps = {
  onClose: () => void;
  inicio: Date | null;
  fin: Date | null;
  tipo: 'Internacional' | 'Nacional';
  region?: string;
  error: string;
  showBreakdown: boolean;
  legsBuffer: Leg[];
  editingLegIndex: number | null;
  setInicio: (d: Date | null) => void;
  setFin: (d: Date | null) => void;
  setTipo: (t: 'Internacional' | 'Nacional') => void;
  setRegion: (r?: string) => void;
  setShowBreakdown: (b: boolean) => void;
  setLegsBuffer: React.Dispatch<React.SetStateAction<Leg[]>>;
  setEditingLegIndex: (i: number | null) => void;
  handleSave: () => void;
};

export const TripEditFormModal: React.FC<TripEditFormModalProps> = ({
  onClose,
  inicio,
  fin,
  tipo,
  region,
  error,
  showBreakdown,
  legsBuffer,
  editingLegIndex,
  setInicio,
  setFin,
  setTipo,
  setRegion,
  setShowBreakdown,
  setLegsBuffer,
  setEditingLegIndex,
  handleSave,
}) => {
  const { t } = useTranslation();
  // Expose a test-only global helper to set modal state when running E2E tests.
  // Tests can call window.__wsc_setEditModal({ tipo, inicio, fin }) to update the modal.
  // Use canonical helpers for reading persisted values
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__wsc_setEditModal = (p: {
        tipo?: 'Internacional' | 'Nacional';
        inicio?: string;
        fin?: string;
        legs?: Leg[];
        region?: string;
      }) => {
        try {
          if (p.tipo) {
            setTipo(p.tipo);
          }
          if (p.inicio) {
            setInicio(isoToDateInAppTz(p.inicio));
          }
          if (p.fin) {
            setFin(isoToDateInAppTz(p.fin));
          }
          if (Array.isArray(p.legs)) {
            setLegsBuffer(p.legs);
          } else if (p.inicio && p.fin) {
            // Replace legsBuffer with a single leg constructed from provided values
            setLegsBuffer([
              {
                inicio: p.inicio,
                fin: p.fin,
                tipo: p.tipo ?? tipo,
                region: p.region,
              },
            ]);
          }
        } catch (e) {
          // ignore
        }
      };
    }
  }, [setTipo, setInicio, setFin, setLegsBuffer, tipo, region]);
  const resumen =
    inicio && fin
      ? calculatePactoLegsPayment([{ inicio: inicio.toISOString(), fin: fin.toISOString(), tipo }])
      : { dias: [], total: 0, totalFestivos: 0, totalFestiveAmount: 0 };

  const horas = inicio && fin ? (fin.getTime() - inicio.getTime()) / (1000 * 60 * 60) : 0;

  // No debug hooks remain in this component.

  const isMobile = useMediaQuery('(max-width: 820px)');
  // High z-index popover props used to ensure pickers appear above fixed UI (cookie banner, notifications)
  const popoverHighZ = { withinPortal: true, hideDetached: false, zIndex: 2147483000 };
  // High z-index modal props for mobile date pickers to appear above context modals
  const modalHighZ = { zIndex: 2147483001 };

  const handleInicioChange = (value: string | null) => {
    const d = value ? new Date(value) : null;
    setInicio(d);
    // keep fin at least equal to inicio
    if (d && (!fin || fin <= d)) {
      setFin(new Date(d));
    }
    // if we're editing an existing leg, update the legsBuffer preview
    if (editingLegIndex !== null) {
      const newIso = d ? d.toISOString() : null;
      setLegsBuffer((prev) => {
        const copy = prev.map((l) => ({ ...l }));
        if (typeof newIso === 'string') {
          // update the current leg's inicio
          if (copy[editingLegIndex]) {
            copy[editingLegIndex].inicio = newIso;
          }
          // also propagate to previous leg's fin to keep continuity
          if (editingLegIndex > 0 && copy[editingLegIndex - 1]) {
            copy[editingLegIndex - 1].fin = newIso;
          }
        }
        return copy;
      });
    }
  };

  const handleFinChange = (value: string | null) => {
    const d = value ? new Date(value) : null;
    setFin(d);
    // keep inicio at most equal to fin
    if (d && (!inicio || d <= inicio)) {
      setInicio(new Date(d));
    }
    // if editing an existing leg, propagate fin to next leg's inicio preview
    if (editingLegIndex !== null) {
      const newIso = d ? d.toISOString() : null;
      setLegsBuffer((prev) => {
        const copy = prev.map((l) => ({ ...l }));
        if (typeof newIso === 'string') {
          // update current leg's fin
          if (copy[editingLegIndex]) {
            copy[editingLegIndex].fin = newIso;
          }
          // propagate to next leg's inicio
          if (copy[editingLegIndex + 1]) {
            copy[editingLegIndex + 1].inicio = newIso;
          }
        }
        return copy;
      });
    }
  };

  return (
    <div data-testid="modal">
      <Stack gap="sm">
        <Group grow={!isMobile} style={isMobile ? { flexDirection: 'column' } : undefined}>
          <DateTimePicker
            id="edit-inicio"
            value={inicio}
            label={t('fechaYHoraInicio')}
            placeholder={t('seleccionaFechaHora')}
            onChange={handleInicioChange}
            locale="es"
            clearable
            required
            // On small screens prefer the modal picker to avoid popover clipping
            // and issues with portal stacking. On larger viewports use a popover.
            // Always set a high zIndex on the popover so it appears above fixed
            // UI (cookie banner, footer, notifications) in edge cases.
            dropdownType={isMobile ? 'modal' : 'popover'}
            modalProps={isMobile ? modalHighZ : undefined}
            popoverProps={isMobile ? undefined : popoverHighZ}
            timePickerProps={isMobile ? undefined : { popoverProps: popoverHighZ }}
            valueFormat={isMobile ? 'DD/MM/YYYY\nHH:mm' : 'DD/MM/YYYY HH:mm'}
            size="md"
          />
          <DateTimePicker
            id="edit-fin"
            value={fin}
            label={t('fechaYHoraFin')}
            placeholder={t('seleccionaFechaHora')}
            onChange={handleFinChange}
            locale="es"
            clearable
            required
            // On small screens prefer the modal picker to avoid popover clipping
            dropdownType={isMobile ? 'modal' : 'popover'}
            modalProps={isMobile ? modalHighZ : undefined}
            popoverProps={isMobile ? undefined : popoverHighZ}
            timePickerProps={isMobile ? undefined : { popoverProps: popoverHighZ }}
            valueFormat={isMobile ? 'DD/MM/YYYY\nHH:mm' : 'DD/MM/YYYY HH:mm'}
            size="md"
            minDate={inicio || undefined}
          />
        </Group>
        <Select
          label={t('tipoDePago')}
          id="edit-tipo"
          data-testid="edit-tipo-select"
          value={tipo}
          onChange={(value) => {
            setTipo((value ?? 'Internacional') as 'Internacional' | 'Nacional');
          }}
          data={[
            {
              value: 'Internacional',
              label: t('tipoInternacionalLabel', { rate: getRate('Internacional') }),
            },
            {
              value: 'Nacional',
              label: t('tipoNacionalLabel', { rate: getRate('Nacional') }),
            },
          ]}
          required
          size="md"
        />
        {tipo === 'Nacional' && (
          <Select
            label={t('regionSoloParaNacional')}
            placeholder={t('seleccionaRegionOpcional')}
            size="md"
            data={getSpainRegionOptions().map((r) => ({ value: r.value ?? '', label: r.label }))}
            value={region ?? ''}
            onChange={(v: string | null) => setRegion(v || undefined)}
          />
        )}
        {error && (
          <Text c="red" role="alert">
            {error}
          </Text>
        )}
        {inicio && fin && horas > 0 && !error && (
          <>
            <Text>
              {t('horas')}: <strong>{horas.toFixed(2)}</strong>
              <br />
              {t('diasTotales')}: <strong>{resumen.dias.length}</strong>
              <br />
              {t('pago')}: <strong>{resumen.total.toFixed(2)} EUR</strong>
            </Text>
            <Group justify="flex-end">
              <Button variant="subtle" size="xs" onClick={() => setShowBreakdown(!showBreakdown)}>
                {showBreakdown ? t('ocultarDesglose') : t('verDesglose')}
              </Button>
            </Group>
            <Collapse in={showBreakdown}>
              <Table striped withRowBorders highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('fechaCol')}</Table.Th>
                    <Table.Th>{t('horasCol')}</Table.Th>
                    <Table.Th>{t('pagoCol')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {resumen.dias.map((d, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{d.fecha}</Table.Td>
                      <Table.Td>{d.horas.toFixed(2)}</Table.Td>
                      <Table.Td>{d.pagoBase.toFixed(2)} EUR</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Collapse>
          </>
        )}
        <Divider my="sm" />
        <Text fw={600}>{t('gestionarTramos')}</Text>
        <Group>
          <Button
            type="button"
            onClick={() => {
              if (!inicio || !fin) {
                return;
              }
              if (fin <= inicio) {
                return;
              }
              const newLeg: Leg = {
                inicio: inicio.toISOString(),
                fin: fin.toISOString(),
                tipo,
                region: tipo === 'Nacional' ? region : undefined,
              };
              if (editingLegIndex !== null && editingLegIndex >= 0) {
                setLegsBuffer((s) => s.map((l, i) => (i === editingLegIndex ? newLeg : l)));
                setEditingLegIndex(null);
              } else {
                setLegsBuffer((s) => [...s, newLeg]);
                // Toggle the trip type after adding a leg. If switching to Internacional, clear region.
                const nextTipo = tipo === 'Internacional' ? 'Nacional' : 'Internacional';
                setTipo(nextTipo);
                if (nextTipo === 'Internacional') {
                  setRegion(undefined);
                }
              }
              setInicio(new Date(newLeg.fin));
              setFin(null);
            }}
          >
            {editingLegIndex !== null ? t('actualizarTramo') : t('anadirTramo')}
          </Button>
          <Button
            color="red"
            variant="light"
            onClick={() => {
              setLegsBuffer([]);
              setEditingLegIndex(null);
            }}
          >
            {t('limpiarTramos')}
          </Button>
        </Group>

        {legsBuffer.length > 0 && (
          <LegsTable
            legs={legsBuffer}
            onEdit={(li: number) => {
              setEditingLegIndex(li);
              const l = legsBuffer[li];
              setInicio(new Date(l.inicio));
              setFin(new Date(l.fin));
              setTipo(l.tipo);
              setRegion(l.region);
            }}
            onDelete={(li: number) => setLegsBuffer((s) => s.filter((_, i) => i !== li))}
            moveUp={(li: number) => {
              if (li === 0) {
                return;
              }
              setLegsBuffer((s) => {
                const copy = [...s];
                [copy[li], copy[li - 1]] = [copy[li - 1], copy[li]];
                return copy;
              });
            }}
            moveDown={(li: number) => {
              if (li === legsBuffer.length - 1) {
                return;
              }
              setLegsBuffer((s) => {
                const copy = [...s];
                [copy[li], copy[li + 1]] = [copy[li + 1], copy[li]];
                return copy;
              });
            }}
          />
        )}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('cancelar')}
          </Button>
          <Button onClick={handleSave} disabled={!!error || legsBuffer.length === 0}>
            {t('guardarCambios')}
          </Button>
        </Group>
      </Stack>
    </div>
  );
};
